import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer } from 'antd';
import Service from '../../../services/graphQlServices';
import { SHOOTER_TIMELINE_QUERY } from '../../../controllers/crewAvailabilityGql';
import moment from 'moment';
import LoadingBar from '../../components/global/loadingBar';

const CHUNK_DAYS = 7;
const MAX_RANGE_DAYS = 30;
const SCROLL_THROTTLE_MS = 200;

const formatDate = (d) => moment(String(d), 'YYYYMMDD').format('ddd, MMM D');
const offsetDate = (base, days) =>
	parseInt(moment(String(base), 'YYYYMMDD').add(days, 'days').format('YYYYMMDD'));

const ShooterSidebar = ({ shooter, anchorDate, onClose, vendorId, cache }) => {
	const navigate = useNavigate();
	const cacheKey = shooter.userId;
	const cached = cache[cacheKey];

	const [shooterDates, setShooterDates] = useState(cached?.dates || []);
	const [loading, setLoading] = useState(!cached);
	const [loadingMore, setLoadingMore] = useState(false);
	const rangeStartRef = useRef(cached?.rangeStart || offsetDate(anchorDate, -CHUNK_DAYS));
	const rangeEndRef = useRef(cached?.rangeEnd || offsetDate(anchorDate, CHUNK_DAYS));
	const fetchingRef = useRef(false);
	const anchorRef = useRef(null);
	const didScrollToAnchor = useRef(false);
	const scrollEnabledRef = useRef(false);
	const scrollContainerRef = useRef(null);
	const scrollRestoreRef = useRef(null);
	const scrollThrottleRef = useRef(null);

	const updateCache = useCallback(
		(dates, rangeStart, rangeEnd) => {
			cache[cacheKey] = { dates, rangeStart, rangeEnd };
		},
		[cache, cacheKey]
	);

	const fetchChunk = useCallback(
		async (start, end, direction) => {
			if (fetchingRef.current) return;
			fetchingRef.current = true;
			if (direction) setLoadingMore(true);

			if (direction === 'before' && scrollContainerRef.current) {
				scrollRestoreRef.current = {
					scrollHeight: scrollContainerRef.current.scrollHeight,
					scrollTop: scrollContainerRef.current.scrollTop,
				};
			}

			try {
				const [success, response] = await Service.query(
					SHOOTER_TIMELINE_QUERY,
					{ startDate: start, endDate: end },
					vendorId,
					localStorage.getItem('usertoken'),
					'vendors'
				);

				if (success) {
					const data = response.data.shooterAvailability;
					const newDates =
						data?.shooters?.find((s) => s.userId === shooter.userId)?.dates || [];

					setShooterDates((prev) => {
						const existingSet = new Set(prev.map((d) => d.date));
						const unique = newDates.filter((d) => !existingSet.has(d.date));
						if (unique.length === 0) {
							scrollRestoreRef.current = null;
							return prev;
						}
						const merged =
							direction === 'before'
								? [...unique, ...prev]
								: [...prev, ...unique];
						const sorted = merged.sort((a, b) => a.date - b.date);
						updateCache(sorted, rangeStartRef.current, rangeEndRef.current);
						return sorted;
					});
				} else {
					scrollRestoreRef.current = null;
				}
			} catch (err) {
				scrollRestoreRef.current = null;
			} finally {
				setLoading(false);
				setLoadingMore(false);
				fetchingRef.current = false;
			}
		},
		[vendorId, shooter, updateCache]
	);

	// Restore scroll position after prepending data
	useLayoutEffect(() => {
		if (scrollRestoreRef.current && scrollContainerRef.current) {
			const { scrollHeight: prevHeight, scrollTop: prevTop } =
				scrollRestoreRef.current;
			const newHeight = scrollContainerRef.current.scrollHeight;
			scrollContainerRef.current.scrollTop =
				prevTop + (newHeight - prevHeight);
			scrollRestoreRef.current = null;
		}
	}, [shooterDates]);

	// Initial fetch — skip if cache hit
	useEffect(() => {
		if (cached) {
			// Cache hit: enable scroll immediately, scroll to anchor
			scrollEnabledRef.current = true;
		} else {
			fetchChunk(rangeStartRef.current, rangeEndRef.current, null);
		}
	}, []);

	// Scroll to anchor after data is ready, then enable infinite scroll
	useEffect(() => {
		if (!loading && !didScrollToAnchor.current && anchorRef.current) {
			setTimeout(() => {
				anchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}, 100);
			didScrollToAnchor.current = true;
			// Enable scroll-triggered fetching after animation settles
			setTimeout(() => {
				scrollEnabledRef.current = true;
			}, 600);
		}
	}, [loading, shooterDates]);

	const handleScroll = useCallback((e) => {
		if (!scrollEnabledRef.current) return;
		if (scrollThrottleRef.current) return;
		scrollThrottleRef.current = setTimeout(() => {
			scrollThrottleRef.current = null;
		}, SCROLL_THROTTLE_MS);

		if (fetchingRef.current) return;
		const { scrollTop, scrollHeight, clientHeight } = e.target;

		if (scrollTop < 60) {
			const daysBack = moment(String(anchorDate), 'YYYYMMDD').diff(
				moment(String(rangeStartRef.current), 'YYYYMMDD'),
				'days'
			);
			if (daysBack < MAX_RANGE_DAYS) {
				const newStart = offsetDate(rangeStartRef.current, -CHUNK_DAYS);
				const oldStart = rangeStartRef.current;
				rangeStartRef.current = newStart;
				fetchChunk(newStart, offsetDate(oldStart, -1), 'before');
			}
		}

		if (scrollHeight - scrollTop - clientHeight < 60) {
			const daysForward = moment(String(rangeEndRef.current), 'YYYYMMDD').diff(
				moment(String(anchorDate), 'YYYYMMDD'),
				'days'
			);
			if (daysForward < MAX_RANGE_DAYS) {
				const oldEnd = rangeEndRef.current;
				const newEnd = offsetDate(rangeEndRef.current, CHUNK_DAYS);
				rangeEndRef.current = newEnd;
				fetchChunk(offsetDate(oldEnd, 1), newEnd, 'after');
			}
		}
	}, [anchorDate, fetchChunk]);

	// Cleanup throttle timer on unmount
	useEffect(() => {
		return () => {
			if (scrollThrottleRef.current) clearTimeout(scrollThrottleRef.current);
		};
	}, []);

	const handleEventClick = useCallback((event) => {
		if (event.workspaceId && event.projectId && event.eventId) {
			navigate(`/${vendorId}/shoots/${event.workspaceId}/${event.projectId}/${event.eventId}`);
		}
	}, [vendorId, navigate]);

	return (
		<Drawer
			title={shooter.name}
			placement="right"
			width={380}
			onClose={onClose}
			open={true}
			className="shooter-sidebar"
		>
			{loading ? (
				<div className="sidebar-loader">
					<LoadingBar />
				</div>
			) : (
				<div
					className="sidebar-timeline"
					ref={scrollContainerRef}
					onScroll={handleScroll}
				>
					{loadingMore && (
						<div className="sidebar-loading-more">Loading...</div>
					)}
					{shooterDates.map((dateEntry) => (
						<div
							key={dateEntry.date}
							className={`sidebar-day ${dateEntry.date === anchorDate ? 'selected' : ''}`}
							ref={dateEntry.date === anchorDate ? anchorRef : null}
						>
							<div className="sidebar-day-header">
								<span>{formatDate(dateEntry.date)}</span>
								{dateEntry.date === anchorDate && (
									<span className="selected-label">Selected</span>
								)}
							</div>
							{!dateEntry.events || dateEntry.events.length === 0 ? (
								<div className="sidebar-no-events">No events</div>
							) : (
								dateEntry.events.map((event, i) => (
									<div
										key={i}
										className="sidebar-event clickable"
										onClick={() => handleEventClick(event)}
									>
										<div className="sidebar-event-name">
											{event.workspaceId && <span className="sidebar-event-workspace">{event.workspaceId} - </span>}
											{event.projectTitle && <span>{event.projectTitle} - </span>}
											<strong>{event.eventName || 'Untitled'}</strong>
										</div>
										<div className="sidebar-event-details">
											{event.reportingTime || event.eventTime || 'Time TBD'}
											{event.durationInHrs && ` \u2022 ${event.durationInHrs}hrs`}
											{event.city && ` \u2022 ${event.city}`}
											{event.roles?.length > 0 &&
												` \u2022 ${event.roles.map((r) => r.role).join(', ')}`}
										</div>
									</div>
								))
							)}
						</div>
					))}
					{loadingMore && (
						<div className="sidebar-loading-more">Loading...</div>
					)}
				</div>
			)}
		</Drawer>
	);
};

export default ShooterSidebar;
